import { ref, computed, watch } from 'vue';
import L from 'leaflet';
import { getRegionCoordinates, hasRegionCoordinates } from '../utils/regionCoordinates';
import { getDepartmentCoordinates, hasDepartmentCoordinates } from '../utils/departmentCoordinates';
import { getCityCoordinates } from '../utils/cityCoordinates';

/**
 * Composable pour gérer les marqueurs des utilisateurs du top 10 sur la carte
 * 
 * Fonctionnalités :
 * - Regroupe les utilisateurs par localisation (région/département/commune)
 * - Affiche le mieux classé par défaut
 * - Au survol, "éclate" les autres utilisateurs en cercle
 * - Adapte l'affichage selon le niveau de zoom (régions/départements/communes)
 * 
 * @param {Ref<Array>} topUsers - Liste réactive des utilisateurs du top 10
 * @param {Ref<string>} currentLevel - Niveau actuel : 'regions', 'departements', 'communes'
 * @param {Ref<Object>} currentCommunesGeoJson - GeoJSON des communes (pour calculer les centroïds)
 * @param {Ref<string>} externalHoveredUsername - Username survolé depuis l'extérieur (ex: depuis la liste)
 * @param {Ref<Object>} leafletMap - Instance Leaflet de la carte
 * @returns {Object} État et méthodes de gestion des marqueurs
 */
export function useTopUserMarkers(
  topUsers,
  currentLevel,
  currentCommunesGeoJson = null,
  externalHoveredUsername = null,
  leafletMap = null
) {
  const normalizeRegionCode = (code) => {
    if (code == null) return null;
    return String(code).trim();
  };

  const normalizeDepartmentCode = (code) => {
    if (code == null) return null;
    let value = String(code).trim().toUpperCase();
    if (/^\d+$/.test(value)) {
      value = value.padStart(2, '0');
    }
    return value;
  };
  // Code de la localisation actuellement survolée (région, département ou commune)
  const hoveredLocationCode = ref(null);

  // Username de l'utilisateur actuellement survolé
  const hoveredUsername = ref(null);

  // Timer pour gérer le délai du mouseout
  let hoverTimeout = null;

  // Synchroniser avec le username survolé depuis l'extérieur (ex: depuis la liste)
  if (externalHoveredUsername) {
    watch(externalHoveredUsername, (newUsername) => {
      hoveredUsername.value = newUsername;
    });
  }

  // Réinitialiser le survol lors du changement de niveau
  watch(currentLevel, () => {
    hoveredLocationCode.value = null;
    hoveredUsername.value = null;
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      hoverTimeout = null;
    }
  });

  const getOffsetCoordinates = (baseCoordinates, angle) => {
    const map = leafletMap?.value;
    const distancePx = 56;

    if (map && typeof map.latLngToLayerPoint === 'function' && typeof map.layerPointToLatLng === 'function') {
      const basePoint = map.latLngToLayerPoint(L.latLng(baseCoordinates[0], baseCoordinates[1]));
      const offsetPoint = L.point(
        basePoint.x + Math.cos(angle) * distancePx,
        basePoint.y + Math.sin(angle) * distancePx
      );
      const offsetLatLng = map.layerPointToLatLng(offsetPoint);
      return [offsetLatLng.lat, offsetLatLng.lng];
    }

    const distanceFallback = 0.08;
    return [
      baseCoordinates[0] + Math.cos(angle) * distanceFallback,
      baseCoordinates[1] + Math.sin(angle) * distanceFallback,
    ];
  };

  /**
   * Récupère les coordonnées et le code de localisation selon le niveau
   * @param {Object} user - Utilisateur 
   * @param {string} level - Niveau actuel
   * @returns {Object|null} { locationCode, coordinates } ou null
   */
  const getLocationInfo = (user, level) => {
    try {
      if (level === 'regions') {
        // Niveau régions : afficher à la région
        const regionCode = normalizeRegionCode(
          user.city?.Department?.Region?.code ??
          user.department?.Region?.code ??
          user.region?.code ??
          null
        );
        if (!hasRegionCoordinates(regionCode)) return null;
        const coords = getRegionCoordinates(regionCode);
        if (!coords || !Array.isArray(coords) || coords.length !== 2) return null;
        return {
          locationCode: String(regionCode),
          locationName: user.region?.name || 'Région inconnue',
          coordinates: coords,
        };
      } else if (level === 'departements') {
        // Niveau départements : afficher au département
        const deptCode = normalizeDepartmentCode(
          user.department?.code ??
          user.city?.Department?.code ??
          null
        );

        if (!hasDepartmentCoordinates(deptCode)) return null;
        const coords = getDepartmentCoordinates(deptCode);
        if (!coords || !Array.isArray(coords) || coords.length !== 2) return null;
        return {
          locationCode: String(deptCode),
          locationName: user.department?.name || 'Département inconnu',
          coordinates: coords,
        };
      } else if (level === 'communes') {
        // Niveau communes : afficher à la commune
        const cityCode = user.city?.inseeCode ?? user.city?.code ?? user.city?.insee ?? null;

        // Essayer de récupérer depuis le cache
        let coords = getCityCoordinates(cityCode);

        // Si pas dans le cache et qu'on a le GeoJSON, calculer
        if (!coords && currentCommunesGeoJson?.value?.features) {
          const feature = currentCommunesGeoJson.value.features.find(
            f => f.properties?.code === cityCode || f.properties?.insee === cityCode
          );
          if (feature) {
            coords = getCityCoordinates(cityCode, feature);
          }
        }

        // Valider les coordonnées
        if (!coords || !Array.isArray(coords) || coords.length !== 2) return null;
        if (typeof coords[0] !== 'number' || typeof coords[1] !== 'number') return null;

        return {
          locationCode: String(cityCode),
          locationName: user.city?.name || 'Commune inconnue',
          coordinates: coords,
        };
      }

      return null;
    } catch (error) {
      console.error('Erreur dans getLocationInfo:', error);
      return null;
    }
  };

  /**
   * Regroupe les utilisateurs par localisation (région, département ou commune)
   * Retourne un Map avec locationCode => [users]
   */
  const usersByLocation = computed(() => {
    if (!topUsers.value || !Array.isArray(topUsers.value) || !currentLevel.value) {
      return new Map();
    }

    const grouped = new Map();
    const level = currentLevel.value;

    try {
      topUsers.value.forEach((user, index) => {
        const locationInfo = getLocationInfo(user, level);

        // Validation stricte : doit avoir locationCode ET coordinates valides
        if (!locationInfo || !locationInfo.coordinates || !Array.isArray(locationInfo.coordinates)) {
          return;
        }

        const userData = {
          id: `user-${index}`,
          username: user.username,
          rank: index + 1,
          amount: user.amount,
          locationCode: locationInfo.locationCode,
          locationName: locationInfo.locationName,
          baseCoordinates: locationInfo.coordinates,
          // Garder les infos complètes pour le tooltip
          region: user.region,
          department: user.department,
          city: user.city,
        };

        if (!grouped.has(locationInfo.locationCode)) {
          grouped.set(locationInfo.locationCode, []);
        }
        grouped.get(locationInfo.locationCode).push(userData);
      });
    } catch (error) {
      console.error('Erreur dans usersByLocation:', error);
    }

    return grouped;
  });

  /**
   * Marqueurs à afficher (regroupés ou éclatés selon le survol)
   */
  const displayableMarkers = computed(() => {
    const markers = [];

    // Sécurité : ne rien afficher si pas de niveau défini
    if (!currentLevel?.value) return markers;

    try {
      usersByLocation.value.forEach((users, locationCode) => {
        // Sécurité : vérifier que users est un tableau valide
        if (!users || !Array.isArray(users) || users.length === 0) return;

        // Normaliser les codes pour la comparaison (tous en string)
        const normalizedLocationCode = String(locationCode);
        const normalizedHoveredCode = hoveredLocationCode.value ? String(hoveredLocationCode.value) : null;
        const isHovered = normalizedHoveredCode === normalizedLocationCode;

        if (!isHovered) {
          // Mode normal : afficher seulement le mieux classé
          const bestUser = users[0]; // Déjà trié par rang

          // Vérifier que les coordonnées existent ET sont valides
          if (!bestUser.baseCoordinates || !Array.isArray(bestUser.baseCoordinates) || bestUser.baseCoordinates.length !== 2) return;
          if (typeof bestUser.baseCoordinates[0] !== 'number' || typeof bestUser.baseCoordinates[1] !== 'number') return;

          markers.push({
            ...bestUser,
            coordinates: bestUser.baseCoordinates,
            isGrouped: users.length > 1,
            groupCount: users.length,
            type: 'main',
          });
        } else {
          // Mode éclaté : afficher tous les utilisateurs en cercle
          users.forEach((user, index) => {
            // Vérifier que les coordonnées existent ET sont valides
            if (!user.baseCoordinates || !Array.isArray(user.baseCoordinates) || user.baseCoordinates.length !== 2) return;
            if (typeof user.baseCoordinates[0] !== 'number' || typeof user.baseCoordinates[1] !== 'number') return;

            if (index === 0) {
              // Le principal reste au centre
              markers.push({
                ...user,
                coordinates: user.baseCoordinates,
                isGrouped: false,
                groupCount: users.length,
                type: 'main',
              });
            } else {
              // Les autres sont disposés en cercle
              // Si 2 utilisateurs : placer à 180° l'un de l'autre
              // Si 3+ utilisateurs : répartir uniformément
              const totalOthers = users.length - 1;
              let angle;

              if (totalOthers === 1) {
                // Un seul autre : placer à 90° (en haut)
                angle = Math.PI / 2;
              } else {
                // Plusieurs autres : répartir en cercle
                angle = ((index - 1) * 2 * Math.PI) / totalOthers;
              }

              const [offsetLat, offsetLng] = getOffsetCoordinates(user.baseCoordinates, angle);

              markers.push({
                ...user,
                coordinates: [offsetLat, offsetLng],
                isGrouped: false,
                groupCount: users.length,
                type: 'expanded',
                centerCoordinates: user.baseCoordinates, // Pour les lignes de connexion
              });
            }
          });
        }
      });
    } catch (error) {
      console.error('Erreur dans displayableMarkers:', error);
    }

    return markers;
  });

  /**
   * Gère l'entrée de la souris sur une localisation (pour éclatement)
   * @param {string} locationCode - Code de la localisation survolée
   * @param {string} username - Nom d'utilisateur survolé
   */
  const handleLocationHover = (locationCode, username) => {
    // Annuler tout timeout en cours
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      hoverTimeout = null;
    }
    // Normaliser en string pour éviter les problèmes de comparaison
    hoveredLocationCode.value = locationCode ? String(locationCode) : null;
    hoveredUsername.value = username;
  };

  /**
   * Gère la sortie de la souris d'une localisation
   * Utilise un délai pour permettre de passer d'un marqueur à l'autre
   */
  const handleLocationLeave = () => {
    // Annuler le timeout précédent s'il existe
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
    }

    // Attendre un peu avant de fermer l'éclatement
    // Cela permet de passer d'un marqueur éclaté à un autre
    hoverTimeout = setTimeout(() => {
      hoveredLocationCode.value = null;
      hoveredUsername.value = null;
      hoverTimeout = null;
    }, 200); // 200ms de délai
  };

  /**
   * Vérifie si une localisation est actuellement survolée
   * @param {string} locationCode - Code de la localisation à vérifier
   * @returns {boolean}
   */
  const isLocationHovered = (locationCode) => {
    return hoveredLocationCode.value === locationCode;
  };

  return {
    displayableMarkers,
    usersByLocation,
    hoveredLocationCode,
    hoveredUsername,
    handleLocationHover,
    handleLocationLeave,
    isLocationHovered,
  };
}
